<template>
  <modal
    class="intake-form-modal"
    name="intake-form-modal"
    transition="pop-out"
    :clickToClose="false"
    height="auto"
    v-model="show"
  >
    <div class="position-relative main-modal-container">
      <div class="vm--top-right-slot border-bottom pb-3" style="z-index: 1">
        <h2
          class="
            position-absolute
            m-3
            pt-1
            mr-5
            text-left
            font-weight-bold
            pointer-none
          "
          style="letter-spacing: 0.1px"
        >
          New Intake
        </h2>
        <button
          class="btn p-0 pr-3 pt-3 border-0"
          :disabled="createIntakeLoading"
          @click="closeModal"
        >
          <i class="fas fa-times fa-lg"></i>
        </button>
      </div>
      <div class="card-body">
        <div class="row border-bottom pb-4">
          <div v-if="from === 'booking-requests'" class="col-12 mb-3">
            <select-control
              v-model="selectedFormType"
              :options="formTypes"
              type="horizontal"
              labelClass="form-type-label"
              wrapClass="align-items-center my-0"
              >Form Type</select-control
            >
          </div>
          <div class="col-12">
            <input-control
              v-model="phoneNumber"
              type="horizontal"
              labelClass="phone-label"
              wrapClass="align-items-center my-0"
              :cleave="cleave.phone"
              >Phone Number</input-control
            >
          </div>
        </div>
        <div class="saving-note-container" v-if="createIntakeLoading">
          <div class="saving-note alert alert-info mb-1 mt-3 py-1" role="alert">
            This may take few moments...
          </div>
        </div>
        <div class="custom-control custom-checkbox mt-3">
          <input
            type="checkbox"
            class="custom-control-input"
            id="open_intake"
            v-model="openIntake"
          />
          <label class="custom-control-label mr-3 mb-3" for="open_intake"
            >Open Intake</label
          >
        </div>

        <div class="mb-2 mt-2 d-flex justify-content-end">
          <save
            :shouldDisable="createIntakeLoading"
            iconType="envelope"
            @click="handleCreate('email')"
            classes="btn btn-black mr-4"
            >Send Email</save
          >
          <save
            :shouldDisable="createIntakeLoading"
            iconType="message"
            @click="handleCreate('sms')"
            classes="btn btn-black mr-4"
            >Send SMS</save
          >
          <save
            :shouldDisable="createIntakeLoading"
            iconType="copy"
            @click="handleCreate('copy')"
            classes="btn btn-theme"
            >Get Intake URL</save
          >
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";

export default {
  name: "CreateIntakeFormModal",
  props: {
    from: {
      type: String,
      default: "patient-profile",
    },
    userId: {
      type: Number,
      required: true,
    },
    userPhoneNumber: {
      type: String,
      default: "",
    },
    formType: {
      type: Number,
      default: 0,
    },
    profileId: {
      type: Number,
      default: null,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "intake-created"],
  data() {
    return {
      createIntakeLoading: false,
      phoneNumber: this.userPhoneNumber,
      selectedFormType: this.formType,
      openIntake: false,
      cleave: {
        phone: { blocks: [20], numericOnly: true },
      },
    };
  },
  created() {
    this.getIntakeCategories({ status: "ACTIVE" });
  },
  computed: {
    ...mapState({
      categories: (state) => state.intakeForms.categories.data,
    }),
    show: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    formTypes: function () {
      return [
        { value: 0, label: "Select Form Type" },
        ...this.categories.map((item) => {
          return {
            value: item.type_id,
            label: item.name,
          };
        }),
      ];
    },
  },
  watch: {
    userPhoneNumber(val) {
      this.phoneNumber = val;
    },
    formType(val) {
      this.selectedFormType = val;
    },
  },
  methods: {
    ...mapActions({
      createIntakeForm: "intakeForms/create",
      getIntakeCategories: "intakeForms/getCategories",
    }),
    closeModal() {
      this.$emit("update:modelValue", false);
    },

    async handleCreate(sendType) {
      if (!this.selectedFormType || this.selectedFormType === 0) {
        return Swal.fire({
          title: "Error",
          text: "Please select a form type",
          icon: "error",
        });
      }

      if (sendType === "sms" && !this.phoneNumber) {
        return Swal.fire({
          title: "Error",
          text: "Please fill out the phone number",
          icon: "error",
        });
      }

      this.createIntakeLoading = true;
      try {
        const data = await this.createIntakeForm({
          userId: this.userId,
          type: this.selectedFormType,
          phoneNumber: this.phoneNumber,
          sendType: sendType,
          domain: `${window.location.host}/intake`,
        });
        if (sendType === "copy") {
          const fullLink = `https://${window.location.host}/intake/${data?.data?.mkey}`;
          navigator.clipboard.writeText(fullLink).then(
            () => {
              Swal.fire({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 5000,
                timerProgressBar: true,
                icon: "success",
                title:
                  "The intake form link has been copied to your clipboard.",
              });
            },
            (err) => {
              console.error("Could not copy text: ", err);
            }
          );
        } else {
          Swal.fire({
            title: "Success",
            text: `Intake form ${
              sendType === "email" ? "email" : "SMS"
            } sent successfully!`,
            icon: "success",
          });
        }

        this.closeModal();
        this.$emit("intake-created", data?.data, this.openIntake);
      } catch (error) {
        console.error(error);
      } finally {
        this.createIntakeLoading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.intake-form-modal {
  .main-modal-container {
    .phone-label,
    .form-type-label {
      max-width: 150px;
      flex: 0 0 150px;
    }
  }

  .ccc-modal-content {
    background-color: #ffffff;
  }

  .v--modal-box {
    overflow: visible;
    max-height: 100%;
    transition: none;

    @media (max-width: 768px) {
      max-height: 96vh;
      overflow: hidden;

      .card-body {
        max-height: calc(96vh - 47px);
        overflow-y: auto;
      }
    }
  }
}
</style>
